import type { UniverseConditionModel, UniverseSearchModel } from '../UniverseCreationModel';
import { FlagConditionModel } from './FlagConditionModel';
import { indicatorEnumeratedValues } from './indicatorEnumeratedValues';
import { RangeConditionModel } from './RangeConditionModel';
import { SetConditionModel } from './SetConditionModel';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { Condition } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import type { QuantitativeConditionOperation } from 'src/features/scranner/types';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';

/**
 * Reconstructs an editable condition model from its wire form. Returns
 * `null` when the indicator referenced by the wire condition is not yet
 * present in `indicators`, or when the wire condition is a `formula`
 * (formula imports require async ref resolution and are not yet supported
 * for hydrate; the caller should surface a warning if a wire formula is
 * dropped).
 */
export function conditionFromWire(
    wire: Condition,
    indicators: ReadonlyArray<ConcreteIndicator>,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseConditionModel | null {
    switch (wire.type) {
        case 'membership': {
            const indicator = indicators.find((i) => i.id === wire.indicator.id);
            if (!indicator) return null;
            return new FlagConditionModel({
                id: wire.id,
                enabled: wire.enabled,
                indicator,
                invert: wire.invert,
                selection: wire.selection,
                searchModel,
                formulaService,
            });
        }
        case 'quantitative': {
            const indicator = indicators.find((i) => i.id === wire.indicator.id);
            if (!indicator) return null;
            // The editor enforces: greaterThan nulls max; lessThan nulls min.
            const operation: QuantitativeConditionOperation =
                wire.min != null && wire.max == null
                    ? 'greaterThan'
                    : wire.min == null && wire.max != null
                      ? 'lessThan'
                      : 'between';
            return new RangeConditionModel({
                id: wire.id,
                enabled: wire.enabled,
                indicator,
                operation,
                min: wire.min,
                max: wire.max,
                selection: wire.selection,
                searchModel,
                formulaService,
            });
        }
        case 'equality': {
            const indicator = indicators.find((i) => i.id === wire.indicator.id);
            if (!indicator) return null;
            return new SetConditionModel({
                id: wire.id,
                enabled: wire.enabled,
                indicator,
                enumeratedValues: indicatorEnumeratedValues(indicator, searchModel),
                values: wire.values,
                selection: wire.selection,
                searchModel,
                formulaService,
            });
        }
        case 'formula':
            return null;
        default:
            throw new UnreachableCaseError(wire);
    }
}
